<template>
  <TransitionRoot as="template" :show="!!show && !!managedFleet">
    <Dialog as="div" class="fixed inset-0 overflow-hidden z-50" @close="emitClose">
      <div class="absolute inset-0 overflow-hidden">
        <DialogOverlay class="absolute inset-0 bg-black opacity-40" />

        <div class="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-4 sm:pl-8">
          <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-500 sm:duration-700"
            enter-from="translate-x-full"
            enter-to="translate-x-0"
            leave="transform transition ease-in-out duration-400 sm:duration-500"
            leave-from="translate-x-0"
            leave-to="translate-x-full"
          >
            <div class="pointer-events-auto w-screen max-w-md">
              <form class="flex h-full flex-col modalShell">
                <!-- HEADER -->
                <div class="shrink-0 modalHeader">
                  <div class="flex items-start justify-between gap-4">
                    <div class="min-w-0">
                      <DialogTitle class="text-lg font-semibold tracking-tight text-white">
                        Zarządzaj pozycją floty
                      </DialogTitle>
                      <p class="mt-1 text-sm text-slate-300">
                        Edytujesz:
                        <span class="font-semibold text-white">
                          {{ managedFleet?.licensePlate || '—' }}
                        </span>
                      </p>
                    </div>

                    <button
                      type="button"
                      @click.prevent="emitClose"
                      class="iconBtn shrink-0"
                      aria-label="Zamknij panel"
                    >
                      <svg class="h-4 w-4 text-slate-200" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path
                          fill-rule="evenodd"
                          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                          clip-rule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>

                <!-- BODY -->
                <div class="min-h-0 flex-1 overflow-y-auto">
                  <div class="space-y-4 p-4 sm:p-5">
                    <!-- BASIC -->
                    <section class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">Dane podstawowe</div>
                          <div class="sectionSub">
                            Zmień dane pojazdu lub naczepy i zapisz aktualizację.
                          </div>
                        </div>
                      </div>

                      <div class="sectionBody space-y-4">
                        <div class="fieldWrap">
                          <label class="fieldLabel">
                            Numer rejestracyjny<span class="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            maxlength="16"
                            v-model="form.licensePlate"
                            :disabled="deleteFleetOnSave"
                            @blur="form.licensePlate = form.licensePlate?.toUpperCase().trim() || ''"
                            :class="inputCls(errors.licensePlate, deleteFleetOnSave)"
                            placeholder="np. POT 1JE6"
                          />
                          <p v-if="errors.licensePlate" class="fieldError">
                            Podaj poprawną rejestrację (min. 3 znaki).
                          </p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">
                            Marka<span class="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            maxlength="40"
                            v-model="form.brand"
                            :disabled="deleteFleetOnSave"
                            :class="inputCls(errors.brand, deleteFleetOnSave)"
                            placeholder="np. Opel"
                          />
                          <p v-if="errors.brand" class="fieldError">
                            Pole nie może być puste.
                          </p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Model</label>
                          <input
                            type="text"
                            maxlength="40"
                            v-model="form.model"
                            :disabled="deleteFleetOnSave"
                            :class="inputCls(false, deleteFleetOnSave)"
                            placeholder="np. Corsa"
                          />
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">Aktywność</label>
                          <select
                            v-model="form.active"
                            :disabled="deleteFleetOnSave"
                            :class="selectCls(deleteFleetOnSave)"
                          >
                            <option :value="true">Aktywny</option>
                            <option :value="false">Nieaktywny</option>
                          </select>
                        </div>
                      </div>
                    </section>

                    <!-- DOCUMENTS (tylko pojazd) -->
                    <section v-if="!isTrailer" class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">Dokumenty i GPS</div>
                          <div class="sectionSub">
                            Zmiana tych pól zaktualizuje snapshoty w aktywnych trasach zawierających ten pojazd.
                          </div>
                        </div>
                      </div>

                      <div class="sectionBody space-y-4">
                        <div class="fieldWrap">
                          <label class="fieldLabel">
                            Numer zezwolenia drogowego / zaświadczenia / licencji<span class="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            maxlength="80"
                            v-model="form.roadPermissionNumber"
                            :disabled="deleteFleetOnSave"
                            :class="inputCls(errors.roadPermissionNumber, deleteFleetOnSave)"
                            placeholder="np. ITD/12345/2026"
                          />
                          <p v-if="errors.roadPermissionNumber" class="fieldError">
                            Podaj numer zezwolenia/zaświadczenia/licencji.
                          </p>
                        </div>

                        <div class="fieldWrap">
                          <label class="fieldLabel">
                            Numer urządzenia / lokalizatora GPS<span class="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            maxlength="80"
                            v-model="form.gpsTrackerNumber"
                            :disabled="deleteFleetOnSave"
                            :class="inputCls(errors.gpsTrackerNumber, deleteFleetOnSave)"
                            placeholder="np. GPS-000123"
                          />
                          <p v-if="errors.gpsTrackerNumber" class="fieldError">
                            Podaj numer urządzenia/lokalizatora GPS.
                          </p>
                        </div>
                      </div>
                    </section>

                    <!-- CHAMBERS -->
                    <section class="sectionCard">
                      <div class="sectionHead">
                        <div>
                          <div class="sectionTitle">
                            Komory cysterny
                            <span v-if="isTrailer" class="text-red-400 text-xs font-normal ml-1">(wymagane min. 1)</span>
                          </div>
                          <div class="sectionSub">
                            Edytuj komory i pojemność każdej z nich.
                            <template v-if="!isTrailer">
                              Dla ciągników (bez cysterny) możesz pominąć tę sekcję.
                            </template>
                            <template v-else>
                              Naczepa musi posiadać co najmniej jedną komorę.
                            </template>
                          </div>
                        </div>
                      </div>

                      <div class="sectionBody space-y-3">
                        <!-- Chamber list -->
                        <div
                          v-for="(chamber, idx) in form.chambers"
                          :key="idx"
                          class="chamberRow"
                        >
                          <div class="chamberIndex">{{ idx + 1 }}</div>
                          <div class="chamberInputWrap">
                            <input
                              type="number"
                              min="1"
                              max="100000"
                              step="1"
                              v-model="chamber.capacity"
                              :disabled="deleteFleetOnSave"
                              :class="inputCls(errors.chambers && errors.chambers[idx], deleteFleetOnSave)"
                              placeholder="Pojemność (L)"
                            />
                            <p v-if="errors.chambers && errors.chambers[idx]" class="fieldError">
                              Podaj pojemność od 1 do 100 000 L.
                            </p>
                          </div>
                          <button
                            type="button"
                            @click.prevent="removeChamber(idx)"
                            :disabled="deleteFleetOnSave"
                            :class="['chamberRemoveBtn', deleteFleetOnSave ? 'chamberRemoveBtnDisabled' : '']"
                            aria-label="Usuń komorę"
                          >
                            <svg class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                            </svg>
                          </button>
                        </div>

                        <p v-if="errors.chambersRequired" class="fieldError">
                          Naczepa wymaga co najmniej jednej komory.
                        </p>

                        <!-- Add chamber button -->
                        <button
                          type="button"
                          @click.prevent="addChamber"
                          :disabled="deleteFleetOnSave"
                          :class="['addChamberBtn', deleteFleetOnSave ? 'addChamberBtnDisabled' : '']"
                        >
                          <svg class="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd" />
                          </svg>
                          Dodaj komorę
                        </button>

                        <!-- Total capacity -->
                        <div class="capacitySummary" v-if="form.chambers.length > 0">
                          <div class="capacitySummaryLabel">Łączna pojemność zbiornika</div>
                          <div class="capacitySummaryValue">{{ totalCapacity.toLocaleString('pl-PL') }} L</div>
                        </div>
                      </div>
                    </section>

                    <!-- DELETE -->
                    <section class="sectionCard sectionCardDanger">
                      <div class="sectionHead sectionHeadDanger">
                        <div>
                          <div class="sectionTitle text-red-300">Usuń pozycję floty</div>
                          <div class="sectionSub">
                            Ta akcja usunie wpis z kolekcji i nie będzie można go przywrócić bez backupu.
                          </div>
                        </div>
                      </div>

                      <div class="sectionBody">
                        <label class="dangerToggle">
                          <input
                            v-model="deleteFleetOnSave"
                            type="checkbox"
                            class="dangerCheckbox"
                          />
                          <span class="dangerToggleText">
                            Chcę usunąć tę pozycję – wiem co robię.
                          </span>
                        </label>

                        <p v-if="deleteFleetOnSave" class="dangerHint">
                          Tryb usuwania jest aktywny. Pola edycji zostały zablokowane do czasu odznaczenia opcji.
                        </p>
                      </div>
                    </section>
                  </div>
                </div>

                <!-- FOOTER -->
                <div class="shrink-0 border-t border-slate-700 bg-slate-800 px-4 py-4">
                  <div class="flex items-center justify-between gap-3">
                    <div class="text-xs text-slate-400">
                      <span v-if="loading" class="text-slate-200">Zapisywanie danych...</span>
                      <span v-else-if="deleteFleetOnSave" class="text-red-300">
                        Uwaga: aktywny tryb usuwania pozycji.
                      </span>
                      <span v-else>
                        Sprawdź dane i zapisz zmiany albo usuń pozycję.
                      </span>
                    </div>

                    <div class="flex items-center gap-2">
                      <button
                        @click.prevent="emitClose"
                        v-if="!loading"
                        type="button"
                        class="btnSecondary"
                      >
                        Zamknij
                      </button>

                      <button
                        v-if="!deleteFleetOnSave && !loading"
                        @click.prevent="validateAndSave"
                        type="button"
                        class="btnPrimary"
                      >
                        Aktualizuj
                      </button>

                      <button
                        v-if="deleteFleetOnSave && !loading"
                        type="button"
                        @click.prevent="deleteFleet"
                        class="btnDanger"
                      >
                        Usuń
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { db } from '@/firebase/gfbconf.js'

export default {
  name: 'ModalManageFleet',
  components: { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot },
  props: {
    show: { type: Boolean, default: false },
    managedFleet: { type: Object, default: null }
  },
  data() {
    return {
      loading: false,
      deleteFleetOnSave: false,
      form: {
        active: true,
        brand: '',
        model: '',
        licensePlate: '',
        chambers: [],
        roadPermissionNumber: '',
        gpsTrackerNumber: ''
      },
      originalRoadPermissionNumber: '',
      originalGpsTrackerNumber: '',
      errors: {
        brand: false,
        licensePlate: false,
        chambers: {},
        chambersRequired: false,
        roadPermissionNumber: false,
        gpsTrackerNumber: false
      }
    }
  },
  computed: {
    totalCapacity() {
      return this.form.chambers.reduce((sum, c) => {
        const val = Number(c.capacity)
        return sum + (isNaN(val) || val < 0 ? 0 : val)
      }, 0)
    },
    isTrailer() {
      return this.managedFleet ? !this.managedFleet.vehicle : false
    }
  },
  watch: {
    managedFleet: {
      immediate: true,
      handler(nv) {
        if (!nv) return
        this.populateForm(nv)
      }
    },
    show(val) {
      if (val && this.managedFleet) {
        this.populateForm(this.managedFleet)
      }
    }
  },
  methods: {
    populateForm(source) {
      this.deleteFleetOnSave = false
      this.loading = false
      this.resetErrors()

      this.form.active = !!source.active
      this.form.brand = source.brand || ''
      this.form.model = source.model || ''
      this.form.licensePlate = (source.licensePlate || '').toUpperCase()
      this.form.roadPermissionNumber = source.roadPermissionNumber || ''
      this.form.gpsTrackerNumber = source.gpsTrackerNumber || ''
      this.originalRoadPermissionNumber = source.roadPermissionNumber || ''
      this.originalGpsTrackerNumber = source.gpsTrackerNumber || ''

      // Load chambers from source or fall back to legacy tankLitreCapacity
      if (Array.isArray(source.chambers) && source.chambers.length > 0) {
        this.form.chambers = source.chambers.map(c => ({
          capacity: c.capacity != null ? Number(c.capacity) : null
        }))
      } else if (source.tankLitreCapacity && Number(source.tankLitreCapacity) > 0) {
        // Legacy: single tank without chambers — create one chamber with that capacity
        this.form.chambers = [{ capacity: Number(source.tankLitreCapacity) }]
      } else {
        this.form.chambers = []
      }
    },

    inputCls(hasError, disabled) {
      return [
        'inputField',
        hasError ? 'inputFieldError' : '',
        disabled ? 'inputFieldDisabled' : ''
      ]
    },

    selectCls(disabled) {
      return [
        'selectField',
        disabled ? 'inputFieldDisabled' : ''
      ]
    },

    addChamber() {
      this.form.chambers.push({ capacity: null })
    },

    removeChamber(idx) {
      this.form.chambers.splice(idx, 1)
      if (this.errors.chambers) {
        delete this.errors.chambers[idx]
      }
    },

    resetErrors() {
      this.errors.brand = false
      this.errors.licensePlate = false
      this.errors.chambers = {}
      this.errors.chambersRequired = false
      this.errors.roadPermissionNumber = false
      this.errors.gpsTrackerNumber = false
    },

    validate() {
      this.resetErrors()
      let ok = true

      if (!this.form.licensePlate || this.form.licensePlate.trim().length < 3) {
        this.errors.licensePlate = true
        ok = false
      }

      if (!this.form.brand || this.form.brand.trim().length === 0) {
        this.errors.brand = true
        ok = false
      }

      // Pojazd wymaga zezwolenia drogowego i numeru GPS (SENT-105).
      if (!this.isTrailer) {
        if (!this.form.roadPermissionNumber || !this.form.roadPermissionNumber.trim()) {
          this.errors.roadPermissionNumber = true
          ok = false
        }
        if (!this.form.gpsTrackerNumber || !this.form.gpsTrackerNumber.trim()) {
          this.errors.gpsTrackerNumber = true
          ok = false
        }
      }

      // Trailer requires at least 1 chamber
      if (this.isTrailer && this.form.chambers.length === 0) {
        this.errors.chambersRequired = true
        ok = false
      }

      // Validate each chamber capacity
      this.form.chambers.forEach((chamber, idx) => {
        const val = Number(chamber.capacity)
        if (
          chamber.capacity === '' ||
          chamber.capacity === null ||
          isNaN(val) ||
          val < 1 ||
          val > 100000
        ) {
          this.errors.chambers[idx] = true
          ok = false
        }
      })

      return ok
    },

    async validateAndSave() {
      if (!this.validate()) return
      if (!this.managedFleet || !this.managedFleet.id) return

      this.loading = true
      try {
        const id = this.managedFleet.id

        const chambers = this.form.chambers.map((c, idx) => ({
          index: idx,
          capacity: Number(c.capacity)
        }))

        const payload = {
          active: !!this.form.active,
          brand: this.form.brand ? this.form.brand.trim() : '',
          model: this.form.model ? this.form.model.trim() : '',
          licensePlate: (this.form.licensePlate || '').toUpperCase().trim(),
          tankLitreCapacity: this.totalCapacity,
          chambers: chambers
        }

        const newRoadPermission = (this.form.roadPermissionNumber || '').trim()
        const newGpsTracker = (this.form.gpsTrackerNumber || '').trim()

        if (!this.isTrailer) {
          payload.roadPermissionNumber = newRoadPermission
          payload.gpsTrackerNumber = newGpsTracker
        }

        await db.collection('Fleet').doc(id).update(payload)

        // Sync snapshotów w aktywnych trasach, jeśli dane dokumentów pojazdu uległy zmianie.
        if (
          !this.isTrailer &&
          (
            newRoadPermission !== this.originalRoadPermissionNumber ||
            newGpsTracker !== this.originalGpsTrackerNumber
          )
        ) {
          try {
            await this.syncVehicleSnapshotsToActiveRoutes(id, {
              roadPermissionNumber: newRoadPermission,
              gpsTrackerNumber: newGpsTracker
            })
          } catch (syncErr) {
            console.error('Błąd synchronizacji snapshotów tras:', syncErr)
            this.$store.commit('setNotification', {
              show: true,
              head: 'Pojazd zaktualizowany, ale sync tras nie powiódł się',
              subheader: 'Zmiany dokumentów pojazdu nie zostały przeniesione do trasy. Skontaktuj się z administratorem.',
              success: false
            })
          }
        }

        this.$store.commit('setNotification', {
          show: true,
          head: 'Akcja zakończona sukcesem!',
          subheader: 'Dane floty zostały zaktualizowane.',
          success: true
        })

        this.$emit('reloadFleet', id)
        this.emitClose()
      } catch (e) {
        console.error(e)
        this.$store.commit('setNotification', {
          show: true,
          head: 'Błąd zapisu do bazy',
          subheader: 'Spróbuj ponownie lub skontaktuj się z administratorem.',
          success: false
        })
        this.loading = false
      }
    },

    async syncVehicleSnapshotsToActiveRoutes(vehicleId, fields) {
      // Znajdź aktywne trasy (status 1 = zaplanowana, 2 = rozpoczęta) zawierające ten pojazd
      // i zaktualizuj w snapshocie pojazdu pola roadPermissionNumber oraz gpsTrackerNumber.
      const vehicleRef = db.collection('Fleet').doc(vehicleId)

      const snap = await db.collection('Routes')
        .where('vehicle.ref', '==', vehicleRef)
        .where('status', 'in', [1, 2])
        .get()

      if (snap.empty) return

      const batch = db.batch()
      snap.docs.forEach(doc => {
        batch.update(doc.ref, {
          'vehicle.roadPermissionNumber': fields.roadPermissionNumber,
          'vehicle.gpsTrackerNumber': fields.gpsTrackerNumber
        })
      })
      await batch.commit()
    },

    async deleteFleet() {
      if (!this.managedFleet || !this.managedFleet.id) return

      this.loading = true
      try {
        const id = this.managedFleet.id
        await db.collection('Fleet').doc(id).delete()

        this.$store.commit('setNotification', {
          show: true,
          head: 'Akcja zakończona sukcesem!',
          subheader: 'Pozycja floty została usunięta.',
          success: true
        })

        this.$emit('reloadFleet', id)
        this.emitClose()
      } catch (e) {
        console.error(e)
        this.$store.commit('setNotification', {
          show: true,
          head: 'Akcja zakończona niepowodzeniem!',
          subheader: 'Pozycja floty nie została usunięta.',
          success: false
        })
        this.loading = false
      }
    },

    emitClose() {
      this.$emit('closeFleetModal')
      this.loading = false
      this.deleteFleetOnSave = false
      this.resetErrors()
    }
  }
}
</script>

<style lang="scss" scoped>
.modalShell {
  height: 100%;
  background: rgba(30, 41, 59, 0.98);
  border-left: 1px solid rgba(71, 85, 105, 0.8);
  box-shadow: -20px 0 60px rgba(0, 0, 0, 0.35);
}

.modalHeader {
  padding: 20px 20px 18px 20px;
  border-bottom: 1px solid rgba(51, 65, 85, 1);
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.92));
}

.sectionCard {
  overflow: hidden;
  border: 1px solid rgba(71, 85, 105, 0.85);
  border-radius: 22px;
  background: rgba(30, 41, 59, 0.72);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  position: relative;
}

.sectionCardDanger {
  border-color: rgba(127, 29, 29, 0.8);
  background: rgba(69, 10, 10, 0.22);
}

.sectionHead {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 14px 16px;
  border-bottom: 1px solid rgba(51, 65, 85, 0.9);
  background: rgba(51, 65, 85, 0.32);
}

.sectionHeadDanger {
  border-bottom-color: rgba(127, 29, 29, 0.85);
  background: rgba(127, 29, 29, 0.18);
}

.sectionTitle {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
}

.sectionSub {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.5;
  color: rgba(148, 163, 184, 1);
}

.sectionBody {
  padding: 16px;
}

.fieldWrap {
  display: flex;
  flex-direction: column;
}

.fieldLabel {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #e2e8f0;
}

.fieldError {
  margin-top: 6px;
  font-size: 12px;
  color: #f87171;
}

.inputField,
.selectField {
  display: block;
  width: 100%;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 14px;
  font-size: 14px;
  line-height: 1.3;
  transition: all 0.18s ease;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.inputField::placeholder {
  color: rgba(148, 163, 184, 0.75);
}

.inputField:focus,
.selectField:focus {
  outline: none;
  border-color: rgba(99, 102, 241, 0.85);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.inputFieldError {
  border-color: rgba(239, 68, 68, 0.9);
}

.inputFieldDisabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.iconBtn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  width: 38px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(51, 65, 85, 0.8);
  transition: all 0.18s ease;
}

.iconBtn:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnSecondary,
.btnPrimary,
.btnDanger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 16px;
  border-radius: 14px;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.18s ease;
}

.btnSecondary {
  border: 1px solid rgba(100, 116, 139, 0.8);
  background: rgba(51, 65, 85, 0.75);
  color: #f1f5f9;
}

.btnSecondary:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnPrimary {
  border: 1px solid rgba(59, 130, 246, 0.55);
  background: rgba(37, 99, 235, 0.22);
  color: #dbeafe;
}

.btnPrimary:hover {
  background: rgba(37, 99, 235, 0.3);
}

.btnDanger {
  border: 1px solid rgba(239, 68, 68, 0.55);
  background: rgba(220, 38, 38, 0.22);
  color: #fecaca;
}

.btnDanger:hover {
  background: rgba(220, 38, 38, 0.32);
}

.dangerToggle {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.dangerCheckbox {
  height: 16px;
  width: 16px;
  border-radius: 4px;
  accent-color: #dc2626;
}

.dangerToggleText {
  font-size: 12px;
  color: #f8fafc;
  user-select: none;
}

.dangerHint {
  margin-top: 10px;
  font-size: 11px;
  line-height: 1.5;
  color: #fca5a5;
}

/* CHAMBERS */
.chamberRow {
  display: flex;
  align-items: flex-start;
  gap: 10px;
}

.chamberIndex {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 44px;
  border-radius: 10px;
  background: rgba(51, 65, 85, 0.6);
  border: 1px solid rgba(71, 85, 105, 0.7);
  font-size: 12px;
  font-weight: 700;
  color: rgba(148, 163, 184, 1);
}

.chamberInputWrap {
  flex: 1;
  min-width: 0;
}

.chamberRemoveBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(127, 29, 29, 0.5);
  background: rgba(127, 29, 29, 0.18);
  color: #fca5a5;
  transition: all 0.18s ease;
}

.chamberRemoveBtn:hover {
  background: rgba(127, 29, 29, 0.35);
  color: #fecaca;
}

.chamberRemoveBtnDisabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.addChamberBtn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px dashed rgba(100, 116, 139, 0.7);
  background: rgba(51, 65, 85, 0.2);
  color: rgba(148, 163, 184, 1);
  font-size: 12px;
  font-weight: 600;
  transition: all 0.18s ease;
}

.addChamberBtn:hover {
  background: rgba(51, 65, 85, 0.45);
  border-color: rgba(100, 116, 139, 1);
  color: #e2e8f0;
}

.addChamberBtnDisabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.capacitySummary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 14px;
  border-radius: 14px;
  background: rgba(51, 65, 85, 0.35);
  border: 1px solid rgba(71, 85, 105, 0.6);
  margin-top: 4px;
}

.capacitySummaryLabel {
  font-size: 12px;
  font-weight: 600;
  color: rgba(148, 163, 184, 1);
}

.capacitySummaryValue {
  font-size: 14px;
  font-weight: 700;
  color: #ffffff;
}

@media (min-width: 640px) {
  .modalHeader {
    padding: 24px 24px 20px 24px;
  }

  .sectionBody,
  .sectionHead {
    padding-left: 18px;
    padding-right: 18px;
  }
}
</style>